import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PartnerQuoteRequest } from '../../../../../models/partner-quote-request.model';
import { Utils } from '../../../../../utils/utils';
import { AmountUtils } from '../../../../../../services/utils/format-amounts.utils';
import { AlertNotificationsService } from '../../../../../../services/alert-notifications.service';
import { Quote } from '../../../../../models/quote.model';
import { ViewStatus } from '@uranda/ui';
import { AdminService } from '../../services/admin.service';
import { PartnerService } from '../../services/partner.service';
import { PdfViewerService } from '../../../../../../services/pdf-viewer.service';
import { isHandledPermissionError } from '../../../../../interceptor/http-interceptor';

@Component({
  selector: 'app-partner-quote-details',
  templateUrl: './partner-quote-details.component.html',
  styleUrls: ['./partner-quote-details.component.css'],
  standalone: false,
})
export class PartnerQuoteDetailsComponent {
  @Input() partnerOfferRequest?: PartnerQuoteRequest;
  @Input() role: 'admin' | 'partner' = 'partner';
  @Input() quoteId!: number;
  @Input() quote?: Quote;

  viewStatus = ViewStatus.LOADING;
  ViewStatus = ViewStatus;
  AmountUtils = AmountUtils;

  isEditingNote = false;
  editedNote = '';
  originalNote = '';

  constructor(
    private translateService: TranslateService,
    private notificationService: AlertNotificationsService,
    private adminService: AdminService,
    private partnerService: PartnerService,
    private pdfViewer: PdfViewerService
  ) { }

  async ngOnInit() {
    this.viewStatus = ViewStatus.LOADING;

    if (this.role === 'admin') {
      await this.loadQuoteDetails();
    } else {
      this.quote = this.partnerOfferRequest?.quote;
      this.viewStatus = ViewStatus.READY;
    }
  }

  async loadQuoteDetails(): Promise<void> {
    try {
      this.quote = await this.adminService.getQuoteDetails(this.quoteId);

      if (!this.quote) {
        console.error('Quote not found');
      }
    } catch (error) {
      console.error('Error loading quote details:', error);
    } finally {
      this.viewStatus = ViewStatus.READY;
    }
  }

  getCustomerName(): string {
    return Utils.formatLeadName(this.getLeadFromQuote(this.quote!));
  }

  private getLeadFromQuote(quote: Quote) {
    return (
      quote?.leadAssignment?.quoteRequest?.lead ||
      quote?.leadAssignment?.contactRequest?.lead ||
      quote?.lead ||
      this.partnerOfferRequest?.quoteRequest?.lead
    );
  }

  /**
   * Provenienza del paziente (accanto a "Customer"): sempre dal lead, mai dalla località di
   * destinazione della richiesta (quoteRequest.country è dove il paziente cerca cliniche).
   */
  get formattedLocation(): string {
    const lead = this.quote ? this.getLeadFromQuote(this.quote) : this.partnerOfferRequest?.quoteRequest?.lead;
    if (!lead) return '';
    return [
      lead.city,
      lead.district,
      lead.region,
      Utils.resolveCountryName(this.translateService, lead.countryCode, lead.country),
    ].filter((part): part is string => !!part).join(', ');
  }

  toggleEditNote() {
    if (!this.isEditingNote) {
      this.originalNote = this.quote?.internalNote || '';
      this.editedNote = this.originalNote;
      this.isEditingNote = true;
    } else {
      this.cancelEditNote();
    }
  }

  async saveNote() {
    if (!this.quote || !this.partnerOfferRequest) {
      return;
    }

    const hasChanges = this.editedNote !== this.originalNote;

    if (hasChanges) {
      try {
        await this.partnerService.updateQuoteNote(this.quote?.id, this.editedNote);

        this.quote.internalNote = this.editedNote;
        this.originalNote = this.editedNote;

        this.notificationService.success(
          this.translateService.instant('Note updated successfully')
        );
      } catch (error) {
        console.error('Error updating note:', error);
        // updateQuoteNote is gated by MANAGE_QUOTES + requireApprovedPartner on the backend
        // (PartnerController.editQuote): a PERMISSION_REQUIRED/PARTNER_NOT_APPROVED 403 already
        // shows its own warning toast (see http-interceptor).
        if (!isHandledPermissionError(error)) {
          this.notificationService.error(
            this.translateService.instant('Error updating note')
          );
        }
        return;
      }
    }

    this.isEditingNote = false;
  }

  cancelEditNote() {
    this.editedNote = this.originalNote;
    this.isEditingNote = false;
  }

  openPdfViewer(pdfUrl: string) {
    if (!pdfUrl) {
      this.notificationService.warning(
        this.translateService.instant('No PDF available')
      );
      return;
    }

    // Rendered in-page by PdfViewerService: opening a blob: URL with
    // window.open() after an await is silently blocked by the popup blocker on
    // Safari mobile, and tablets do not render a PDF in a browser tab reliably.
    const viewer = this.pdfViewer.open({
      title: this.translateService.instant('Quote'),
      source: { url: pdfUrl },
      loadingLabel: this.translateService.instant('Loading Quote...'),
      download: {
        label: this.translateService.instant('Download PDF'),
        filename: `quote-${this.quote?.id ?? ''}.pdf`,
      },
      onError: () => {
        viewer.close();
        this.notificationService.error(
          this.translateService.instant('Unable to open PDF')
        );
      },
    });
  }
}
